<script lang="ts">
import type { StudyMixCompare1SlideProps } from '$lib/components/study/slide-layout-types'
import StudySlideStatTileCard from '../cards/StudySlideStatTileCard.svelte'
import StudySlide from '../StudySlide.svelte'
import StudySlideContent from '../StudySlideContent.svelte'

/**
 * Mix compare (`s6` ADC-style): left `.hl` / `.bd` + `.cc` segmented strip + row bars; right
 * concentration hero + `StudySlideStatTileCard` grid + `.hb`. `eyebrow`, `headline`, `introHtml`,
 * hero labels, and `insightHtml` use trusted `{@html}` where marked in types.
 */
let props: StudyMixCompare1SlideProps = $props()

const headlineStyle = props.headlineStyle ?? 'margin-bottom:.82rem;'
const introStyle = props.introStyle ?? 'margin-bottom:.95rem;'
const leftCardStyle = props.leftCardStyle ?? 'padding:.85rem 1rem .9rem;'
const leftCardTitleStyle = props.leftCardTitleStyle ?? 'margin-bottom:.55rem;'
const tcStyle = props.tcStyle ?? 'gap:2.1rem;align-items:start;'
const tcWeighted = props.tcWeighted ?? true
const tileVariant = props.statTileVariant ?? 'stripe'
const statTileMax = props.statTileMax ?? 4
const showSegmentTiles = $derived(props.showSegmentTiles ?? true)

function segmentPct(count: number): number {
  const t = props.totalCount
  return t > 0 ? (count / t) * 100 : 0
}

function tileValue(p: number): string {
  return props.formatTilePercent?.(p) ?? `${p.toFixed(1)}%`
}
</script>

<StudySlide
  id={props.id}
  active={props.active}
  extraBottom={props.extraBottom}
  background={props.background}
  slideClass={props.slideClass}
>
  <StudySlideContent
    maxWidth={props.maxWidth ?? '980px'}
    contentClass={props.contentClass}
    style={props.contentStyle}
  >
    <div class="ey">{@html props.eyebrow}</div>
    <div class="tc" class:w={tcWeighted} style={tcStyle}>
      <div>
        <div class="hl" style={headlineStyle}>{@html props.headline}</div>
        <p class="bd" style={introStyle}>{@html props.introHtml}</p>

        <div class="cc" style={leftCardStyle}>
          <div class="ct" style={leftCardTitleStyle}>{@html props.leftCardTitle}</div>
          <div
            style="display:flex;height:22px;border-radius:6px;overflow:hidden;gap:1px;margin-bottom:.72rem;"
          >
            {#each props.segments as { name, count, color }}
              {@const p = segmentPct(count)}
              <div
                style="width:{p.toFixed(2)}%;background:{color};transition:width .4s;"
                title="{name}: {p.toFixed(1)}%"
              ></div>
            {/each}
          </div>
          {#each props.segments as { name, count, color }}
            {@const p = segmentPct(count)}
            <div class="pr">
              <div
                style="width:8px;height:8px;border-radius:2px;background:{color};flex-shrink:0;"
              ></div>
              <div class="pl">{name}</div>
              <div class="pt">
                <div class="pf2" style="width:{p.toFixed(1)}%;background:{color};"></div>
              </div>
              <div class="pv">{p.toFixed(1)}%</div>
            </div>
          {/each}
        </div>
      </div>

      <div style="display:flex;flex-direction:column;gap:.72rem;">
        <div
          style="text-align:center;padding:1.2rem 1rem;background:rgba(17,160,165,.07);border:1px solid var(--gbr);border-radius:10px;"
        >
          <div
            style="font-size:.58rem;color:var(--mu);letter-spacing:.12em;text-transform:uppercase;margin-bottom:.4rem;"
          >
            {@html props.heroLabelHtml}
          </div>
          <div
            style="font-family:'DM Serif Display',serif;font-size:3rem;line-height:1;color:var(--g);margin-bottom:.25rem;"
          >
            {props.heroStat}
          </div>
          <div style="font-size:.7rem;color:var(--t2);line-height:1.5;">
            {@html props.heroCaptionHtml}
          </div>
        </div>

        {#if showSegmentTiles}
          <div style="display:grid;grid-template-columns:1fr 1fr;gap:.58rem;">
            {#each props.segments.slice(0, statTileMax) as { name, count, color }}
              {@const p = segmentPct(count)}
              <StudySlideStatTileCard
                variant={tileVariant}
                stripeColor={color}
                value={tileValue(p)}
                label={name}
              />
            {/each}
          </div>
        {/if}

        <div class="hb">
          <p>
            {@html props.insightHtml}
          </p>
        </div>
      </div>
    </div>
  </StudySlideContent>
</StudySlide>
